import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export const formatDateLabel = (dateStr: string, granularity: 'day' | 'week' | 'month') => {
    const d = dayjs(dateStr);
    if (granularity === 'week') {
        const weekNum = d.isoWeek();
        const weekYear = d.isoWeekYear();
        return `${weekYear}年第${weekNum}周`;
    } else if (granularity === 'month') {
        return d.format('YYYY-MM');
    } else {
        return d.format('MM-DD');
    }
};

export const getAOVTrendOption = (trendData: any[], granularity: 'day' | 'week' | 'month') => {
    if (!trendData.length) return {};

    // Calculate AOV for each data point
    const aovData = trendData.map(d => {
        const orders = d.order_items || d.sessions || 0;
        const sales = d.sales || 0;
        return orders > 0 ? parseFloat((sales / orders).toFixed(2)) : 0;
    });

    return {
        title: {
            text: '客单价趋势',
            textStyle: { fontSize: 16, fontWeight: 600, color: '#1f2937' },
            left: 'center'
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            textStyle: { color: '#374151' },
            formatter: (params: any) => {
                const dataIndex = params[0]?.dataIndex;
                if (dataIndex === undefined) return '';
                const item = trendData[dataIndex];
                const d = dayjs(item.date);
                let dateLabel: string;
                if (granularity === 'day') {
                    dateLabel = d.format('YYYY-MM-DD');
                } else if (granularity === 'week') {
                    dateLabel = `${d.isoWeekYear()}年第${d.isoWeek()}周`;
                } else {
                    dateLabel = d.format('YYYY年MM月');
                }
                return `<div style="font-weight:600;margin-bottom:8px">${dateLabel}</div>` +
                    params.map((p: any) => `${p.marker} ${p.seriesName}: <b>$${p.value?.toFixed(2) ?? '-'}</b>`).join('<br/>');
            }
        },
        legend: {
            data: ['客单价'],
            bottom: 0,
            textStyle: { color: '#6b7280' }
        },
        grid: { left: '3%', right: '4%', bottom: '12%', top: '15%', containLabel: true },
        xAxis: {
            type: 'category',
            data: trendData.map(d => formatDateLabel(d.date, granularity)),
            axisLabel: { rotate: granularity === 'day' ? 45 : 0, color: '#6b7280', fontSize: 11 },
            axisLine: { lineStyle: { color: '#e5e7eb' } },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            name: '客单价 ($)',
            nameTextStyle: { color: '#6b7280', fontSize: 11 },
            axisLabel: { color: '#6b7280', formatter: (v: number) => '$' + v.toFixed(0) },
            splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
            axisLine: { show: false }
        },
        series: [
            {
                name: '客单价',
                type: 'line',
                data: aovData,
                smooth: true,
                symbol: 'circle',
                symbolSize: 8,
                lineStyle: { width: 3, color: '#f59e0b' },
                itemStyle: { color: '#f59e0b', borderWidth: 2, borderColor: '#fff' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: 'rgba(245, 158, 11, 0.25)' },
                            { offset: 1, color: 'rgba(245, 158, 11, 0.02)' }
                        ]
                    }
                },
                markLine: {
                    silent: true,
                    data: [
                        { type: 'average', name: '平均值' }
                    ],
                    lineStyle: { color: '#f59e0b', type: 'dashed' },
                    label: { formatter: '平均: ${c}', color: '#f59e0b' }
                }
            }
        ]
    };
};
export const getTrendOption = (trendData: any[], granularity: 'day' | 'week' | 'month') => {
    if (!trendData.length) return {};
    return {
        title: {
            text: '销售趋势',
            textStyle: { fontSize: 16, fontWeight: 600, color: '#1f2937' },
            left: 'center'
        },
        tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            textStyle: { color: '#374151' },
            formatter: (params: any) => {
                const dataIndex = params[0]?.dataIndex;
                if (dataIndex === undefined) return '';
                const item = trendData[dataIndex];
                const d = dayjs(item.date);
                let dateLabel: string;
                if (granularity === 'day') {
                    dateLabel = d.format('YYYY-MM-DD');
                } else if (granularity === 'week') {
                    dateLabel = `${d.isoWeekYear()}年第${d.isoWeek()}周`;
                } else {
                    dateLabel = d.format('YYYY年MM月');
                }
                return `<div style="font-weight:600;margin-bottom:8px">${dateLabel}</div>` +
                    params.map((p: any) => `${p.marker} ${p.seriesName}: <b>${p.value?.toLocaleString() ?? '-'}</b>`).join('<br/>');
            }
        },
        legend: {
            data: ['销售额', '销量'],
            bottom: 0,
            textStyle: { color: '#6b7280' }
        },
        grid: { left: '3%', right: '4%', bottom: '12%', top: '15%', containLabel: true },
        xAxis: {
            type: 'category',
            data: trendData.map(d => formatDateLabel(d.date, granularity)),
            axisLabel: { rotate: granularity === 'day' ? 45 : 0, color: '#6b7280', fontSize: 11 },
            axisLine: { lineStyle: { color: '#e5e7eb' } },
            axisTick: { show: false }
        },
        yAxis: [
            {
                type: 'value',
                name: '销售额 ($)',
                nameTextStyle: { color: '#6b7280', fontSize: 11 },
                axisLabel: { color: '#6b7280', formatter: (v: number) => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v },
                splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
                axisLine: { show: false }
            },
            {
                type: 'value',
                name: '销量',
                nameTextStyle: { color: '#6b7280', fontSize: 11 },
                axisLabel: { color: '#6b7280' },
                splitLine: { show: false },
                axisLine: { show: false }
            }
        ],
        series: [
            {
                name: '销售额',
                type: 'line',
                data: trendData.map(d => d.sales),
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: { width: 3, color: '#6366f1' },
                itemStyle: { color: '#6366f1', borderWidth: 2, borderColor: '#fff' },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: 'rgba(99, 102, 241, 0.25)' },
                            { offset: 1, color: 'rgba(99, 102, 241, 0.02)' }
                        ]
                    }
                }
            },
            {
                name: '销量',
                type: 'bar',
                yAxisIndex: 1,
                data: trendData.map(d => d.units),
                barMaxWidth: 30,
                itemStyle: {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: '#a5f3fc' },
                            { offset: 1, color: '#22d3ee' }
                        ]
                    },
                    borderRadius: [4, 4, 0, 0]
                }
            }
        ]
    };
};
